<template>
  <template
    v-if="
      flags.enabledDsAttendancePolicy ||
      flags.enabledHandoverV2 ||
      flags.enabledFacialBiometry ||
      flags.enabledCheckValidation ||
      flags.enabledPriorityPayments ||
      flags.enabledProfessionalDocuments
    "
  >
    <Tabs v-model="activeTab" :tabs="computedTabList">
      <template v-if="flags.enabledHandoverV2" #handoverApprovals>
        <HandoverApprovals />
      </template>

      <template #exceptionality>
        <AuditoriumExceptionalities />
      </template>

      <template v-if="flags.enabledDsAttendancePolicy" #attendance>
        <DsAttendance />
      </template>

      <template v-if="flags.enabledFacialBiometry" #facialBiometry>
        <FacialBiometry />
      </template>

      <template v-if="flags.enabledCheckValidation" #checkValidation>
        <CheckValidation />
      </template>

      <!-- Com o hub desligado, a rota /aprovacoes/pagamentos-prioritarios cai
           aqui (`?tab=priorityPayments`, ver Hub/queues.ts). -->
      <template v-if="flags.enabledPriorityPayments" #priorityPayments>
        <PriorityPayments />
      </template>

      <template v-if="flags.enabledProfessionalDocuments" #professionalDocuments>
        <ProfessionalDocuments />
      </template>
    </Tabs>
  </template>

  <AuditoriumExceptionalities v-else />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useRoute, useRouter } from 'vue-router'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import AuditoriumExceptionalities from './AuditoriumExceptionalities/index.vue'
import DsAttendance from './DsAttendance/Index.vue'
import HandoverApprovals from './HandoverApprovals/Index.vue'
import FacialBiometry from './FacialBiometry/Index.vue'
import CheckValidation from './CheckValidation/Index.vue'
import PriorityPayments from './PriorityPayments/Index.vue'
import ProfessionalDocuments from './ProfessionalDocuments/Index.vue'
import { approvalsQueueAuthorizations, findApprovalsQueue, isApprovalsQueueKey } from './Hub/queues'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'

const headerStore = useHeaderStore()
const { flags } = useFeatureFlags()
const route = useRoute()
const router = useRouter()

const permissionsStore = usePermissionsStore()

const priorityPaymentsQueue = findApprovalsQueue('priorityPayments')
const professionalDocumentsQueue = findApprovalsQueue('professionalDocuments')

// Mesma regra da guarda de rota de cada fila: aba só do que o usuário consegue
// abrir (o perfil Extrator, por exemplo, só lê o Registro de Plantão).
const canAccessTab = (tab: TabItem) =>
  !isApprovalsQueueKey(tab.value) ||
  approvalsQueueAuthorizations(findApprovalsQueue(tab.value)).some((authorization) =>
    permissionsStore.checkAnyRessources(authorization)
  )

const allTabs = computed<TabItem[]>(() => {
  const tabs: TabItem[] = []
  if (flags.enabledHandoverV2) {
    tabs.push({ label: 'Trocas pelo aplicativo', value: 'handoverApprovals' })
  }
  tabs.push({ label: 'Pedidos de excepcionalidade', value: 'exceptionality' })
  if (flags.enabledDsAttendancePolicy) {
    tabs.push({
      label: 'Registro de plantão',
      value: 'attendance',
      testId: 'ds-attendance-tab-button'
    })
  }
  if (flags.enabledFacialBiometry) {
    tabs.push({
      label: 'Biometria facial',
      value: 'facialBiometry',
      testId: 'facial-biometry-tab-button'
    })
  }
  if (flags.enabledCheckValidation) {
    tabs.push({
      label: 'Avaliação de Evidências',
      value: 'checkValidation',
      testId: 'check-validation-tab-button'
    })
  }
  if (flags.enabledPriorityPayments) {
    tabs.push({
      label: priorityPaymentsQueue.pageTitle,
      value: 'priorityPayments',
      testId: 'priority-payments-tab-button'
    })
  }
  if (flags.enabledProfessionalDocuments) {
    tabs.push({
      label: professionalDocumentsQueue.pageTitle,
      value: 'professionalDocuments',
      testId: 'professional-documents-tab-button'
    })
  }
  return tabs
})

const permittedTabs = computed(() => allTabs.value.filter(canAccessTab))
// Sem nenhuma aba permitida, mantém a lista das flags (a guarda da rota já barra
// quem não tem acesso a nada — nunca renderizar um Tabs vazio).
const computedTabList = computed<TabItem[]>(() =>
  permittedTabs.value.length > 0 ? permittedTabs.value : allTabs.value
)

const preferredDefaultTab = flags.enabledHandoverV2 ? 'handoverApprovals' : 'exceptionality'
const defaultTab = computedTabList.value.some((tab) => tab.value === preferredDefaultTab)
  ? preferredDefaultTab
  : (computedTabList.value[0]?.value ?? preferredDefaultTab)

// `?tab=` de uma aba desligada (link antigo, flag que caiu) abriria um slot
// vazio: cai na aba padrão.
const requestedTab = route.query.tab as string | undefined
const activeTab = ref(
  requestedTab && computedTabList.value.some((tab) => tab.value === requestedTab)
    ? requestedTab
    : defaultTab
)

const headerInfo: Record<string, { title: string; description: string }> = {
  handoverApprovals: {
    title: 'Trocas pelo aplicativo',
    description: 'Avalie e aprove solicitações de trocas e repasses de plantão'
  },
  exceptionality: {
    title: 'Pedidos de excepcionalidade',
    description:
      'Analise, aprove ou recuse solicitações excepcionais vinculadas a escalas e plantões'
  },
  attendance: {
    title: 'Registro de plantão',
    description: 'Visualize e gerencie os registros de plantão dos colaboradores'
  },
  facialBiometry: {
    title: 'Biometria facial',
    description: 'Gerencie a validação de biometria facial'
  },
  checkValidation: {
    title: 'Avaliação de Evidências',
    description: 'Decida sobre os registros de presença lançados manualmente no painel'
  },
  priorityPayments: {
    title: priorityPaymentsQueue.pageTitle,
    description: priorityPaymentsQueue.pageDescription
  },
  professionalDocuments: {
    title: professionalDocumentsQueue.pageTitle,
    description: professionalDocumentsQueue.pageDescription
  }
}

const breadcrumb = { label: 'Aprovações', route: '/aprovacoes' }

watch(
  activeTab,
  (newTab) => {
    const info = headerInfo[newTab]
    if (info) {
      headerStore.setTitle(info.title, info.description, breadcrumb)
    }
  },
  { immediate: true }
)

watch(activeTab, (newTab) => {
  router.replace({ query: { tab: newTab } })
})
</script>
